import React from "react";
import { FormField, Select, Popover } from "@amzn/awsui-components-react";
import { useValidationUtils } from "./ValidationUtils";

interface ValidatedSelectFieldProps {
  label: string;
  fieldName: string;
  value: string | null;
  options: Array<{ label: string; value: string }>;
  placeholder: string;
  onChange: (value: string) => void;
  useCaseName?: string | null;
  uslLineIndex?: number | null;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
}

export const ValidatedSelectField: React.FC<ValidatedSelectFieldProps> = ({
  label,
  fieldName,
  value,
  options,
  placeholder,
  onChange,
  useCaseName = null,
  uslLineIndex = null,
  validationUtils,
  onFocus,
  onBlur,
}) => {
  const {
    getFieldTooltipContent,
    getFieldValidationProps,
    getFieldErrors,
    getFieldTextColor,
  } = validationUtils;
  const tooltipContent = getFieldTooltipContent(
    fieldName,
    useCaseName,
    uslLineIndex,
  );
  const validationProps = getFieldValidationProps(
    fieldName,
    useCaseName,
    uslLineIndex,
  );

  // Use the same logic as text color to determine if field should have yellow border
  const textColor = getFieldTextColor(fieldName, useCaseName, uslLineIndex);
  const hasWarning = textColor === "#855900"; // Yellow color indicates deriving attribute

  const handleFocus = () => {
    // Only trigger focus for derived attributes (red fields)
    const errors = getFieldErrors(fieldName, useCaseName, uslLineIndex);
    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );

    if (hasErrorField && onFocus) {
      onFocus(fieldName, useCaseName, uslLineIndex);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const selectComponent = (
    <div
      style={{
        border: hasWarning ? "2px solid #855900" : "none",
        borderRadius: hasWarning ? "4px" : "0",
        padding: hasWarning ? "0" : "0",
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Select
        selectedOption={value ? { label: value, value } : null}
        onChange={({ detail }) => onChange(detail.selectedOption?.value || "")}
        options={options}
        placeholder={placeholder}
        {...validationProps}
      />
    </div>
  );

  return (
    <FormField label={label}>
      {tooltipContent ? (
        <Popover
          renderWithPortal
          size="large"
          position="top"
          triggerType="custom"
          dismissButton={false}
          content={tooltipContent}
        >
          {selectComponent}
        </Popover>
      ) : (
        selectComponent
      )}
    </FormField>
  );
};
